import { makeArticle, saintQuestionRelated } from "./learnFactory";

export const pageSaintSessions = makeArticle({
  slug: "how-many-saint-tms-sessions",
  title: "How Many SAINT® TMS Sessions Are There? | Neuro Wellness",
  description:
    "SAINT® TMS delivers 50 sessions over 5 consecutive days — 10 sessions per day with 50-minute breaks. Learn why the schedule is accelerated and what each session involves.",
  canonical: "/learn/how-many-saint-tms-sessions",
  h1: "How Many SAINT® TMS Sessions Are There?",
  breadcrumbLabel: "How Many SAINT® Sessions",
  shortAnswer:
    "SAINT® TMS delivers 50 sessions over five consecutive days. Each day includes ten sessions of about ten minutes each, separated by roughly 50-minute breaks. This is significantly more sessions per day than conventional TMS, which delivers one session per day over about six weeks. The accelerated schedule is a defining feature of the SAINT® protocol.",
  keyTakeaways: [
    "SAINT® delivers 50 sessions total — 10 per day for 5 consecutive days.",
    "Each session lasts about 10 minutes, with 50-minute breaks between sessions.",
    "Conventional TMS delivers about 36 sessions over 6 weeks (one per day).",
    "Patients spend most of each treatment day at the clinic but remain awake.",
    "No anesthesia or sedation is used during SAINT® sessions.",
  ],
  sections: [
    {
      heading: "The 50-Session Schedule",
      paragraphs: [
        "SAINT® delivers 50 intermittent theta burst stimulation (iTBS) sessions across five consecutive weekdays. Each day follows the same structure: ten sessions of about ten minutes each, with approximately 50-minute breaks between them. The breaks are not downtime — they are part of the protocol, allowing the brain to consolidate the effects of each stimulation block.",
        "Patients typically arrive in the morning and stay at the clinic for most of the day. Between sessions, most patients read, work on a laptop, rest, or talk with family. The clinic provides a comfortable environment for these breaks.",
      ],
    },
    {
      heading: "Why Multiple Sessions Per Day",
      paragraphs: [
        "The accelerated schedule is based on research from Stanford University suggesting that delivering multiple stimulation sessions per day with rest intervals can produce a faster and larger antidepressant effect than once-daily stimulation. The 50-minute break length was chosen to align with the brain's natural consolidation cycles.",
        "This is different from simply doing more TMS faster. The combination of fMRI-guided targeting, theta burst pulses, and the specific rest interval schedule is what defines the SAINT® protocol.",
      ],
    },
    {
      heading: "How This Compares to Conventional TMS",
      paragraphs: [
        "Conventional TMS delivers one session per day, five days a week, for about four to six weeks — roughly 36 sessions total. Each session lasts about 19 to 37 minutes depending on the protocol. SAINT® compresses more sessions into five days by using shorter theta burst sessions and an accelerated daily schedule.",
      ],
      links: [
        {
          to: "/learn/saint-vs-traditional-tms",
          text: "Compare SAINT® vs traditional TMS",
        },
      ],
    },
  ],
  faqs: [
    {
      question: "How many SAINT sessions are there per day?",
      answer:
        "SAINT® delivers 10 sessions per day for 5 consecutive days, totaling 50 sessions. Each session lasts about 10 minutes, with 50-minute breaks between sessions. Patients spend most of each day at the clinic.",
    },
    {
      question: "Why are there multiple SAINT sessions per day?",
      answer:
        "Research from Stanford found that delivering multiple theta burst sessions per day with 50-minute rest intervals produced a faster and larger antidepressant effect than once-daily stimulation. The breaks allow the brain to consolidate each session's effects before the next one begins.",
    },
    {
      question: "How long is each SAINT session?",
      answer:
        "Each SAINT® session lasts about 10 minutes and delivers 1,800 magnetic pulses using intermittent theta burst stimulation. Including setup and positioning, each session block takes about 15 to 20 minutes, followed by a roughly 50-minute break.",
    },
    {
      question: "Do I get all 50 sessions in one week?",
      answer:
        "Yes. SAINT® is delivered over 5 consecutive weekdays, with 10 sessions each day. The full 50-session course is completed in one week. This is one of the main differences from conventional TMS, which spreads about 36 sessions over 6 weeks.",
    },
    {
      question: "Can I take breaks between treatment days?",
      answer:
        "SAINT® is designed to be delivered on 5 consecutive days. Skipping days disrupts the accelerated protocol. If you have a scheduling conflict, discuss it with the treatment team before starting. The five-day schedule is part of what makes SAINT® distinct from standard TMS.",
    },
  ],
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression. Am J Psychiatry. 2020;177(8):716-726.",
      url: "https://pubmed.ncbi.nlm.nih.gov/32552917/",
    },
  ],
  relatedLinks: saintQuestionRelated,
});
